import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { toast as sonnerToast } from 'sonner';
import { useOrganization } from '@/hooks/useOrganization';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME_PRODUCTS } from '@/lib/queryCacheConfig';
import {
  productModifierCategoriesService,
  type ProductModifierCategoryInput,
} from '@/services/products/productModifierCategoriesService';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import type { ProductModifierCategory } from '@/types';

export const useProductModifierCategories = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { translator } = useContext(LanguageProviderContext);
  const orgId = currentOrganization?.id ?? '';

  const { data: categories = [], isPending: isLoading, error } = useQuery({
    queryKey: queryKeys.productModifierCategories(orgId),
    queryFn: async () => {
      if (!currentOrganization) return [];
      return productModifierCategoriesService.getProductModifierCategories(currentOrganization.id);
    },
    enabled: !!currentOrganization,
    staleTime: STALE_TIME_PRODUCTS.productModifierCategories,
  });

  const invalidateCategories = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.productModifierCategories(orgId) });
  };

  const createCategory = useMutation({
    mutationFn: async (category: ProductModifierCategoryInput) => {
      if (!currentOrganization) throw new Error('No organization selected');
      return productModifierCategoriesService.createProductModifierCategory(category, currentOrganization.id);
    },
    onSuccess: () => {
      invalidateCategories();
      sonnerToast.success(translator('Products.successCreateModifierCategory'));
    },
    onError: (mutationError: Error & { code?: string }) => {
      if (mutationError.code === '23505') {
        sonnerToast.error(translator('Products.modifierCategoryDuplicate'));
        return;
      }
      sonnerToast.error(translator('Errors.unexpectedError'), {
        description: mutationError.message,
      });
    },
  });

  const updateCategory = useMutation({
    mutationFn: async (category: ProductModifierCategoryInput & Pick<ProductModifierCategory, 'id'>) => {
      return productModifierCategoriesService.updateProductModifierCategory(category);
    },
    onSuccess: () => {
      invalidateCategories();
      sonnerToast.success(translator('Products.successUpdateModifierCategory'));
    },
    onError: (mutationError: Error & { code?: string }) => {
      if (mutationError.code === '23505') {
        sonnerToast.error(translator('Products.modifierCategoryDuplicate'));
        return;
      }
      sonnerToast.error(translator('Errors.unexpectedError'), {
        description: mutationError.message,
      });
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (categoryId: string) => {
      return productModifierCategoriesService.deleteProductModifierCategory(categoryId);
    },
    onSuccess: () => {
      invalidateCategories();
      queryClient.invalidateQueries({ queryKey: queryKeys.productModifiers(orgId) });
      sonnerToast.success(translator('Products.successDeleteModifierCategory'), {
        description: translator('Products.successDeleteModifierCategoryDescription'),
      });
    },
    onError: (mutationError: Error & { code?: string }) => {
      sonnerToast.error(translator('Errors.unexpectedError'), {
        description: mutationError.message,
      });
    },
  });

  return {
    categories,
    isLoading,
    error,
    createCategory: createCategory.mutate,
    updateCategory: updateCategory.mutate,
    deleteCategory: deleteCategory.mutate,
    isCreating: createCategory.isPending,
    isUpdating: updateCategory.isPending,
    isDeleting: deleteCategory.isPending,
  };
};
